import React, { useState } from 'react';
import { Database, Plus, RefreshCw, AlertTriangle, CheckCircle, ShieldCheck, Mail, Globe, Activity, Search, X, Edit3, Power, ExternalLink } from 'lucide-react';
import { dataSourceEngine } from '../../services/data/dataSourceEngine';
import { DataSource, DataSourceAlert, DataSourceCategory, DataSourceStatus, AppView } from '../../types';

interface DataSourceManagementViewProps {
  onNavigateToView: (view: AppView) => void;
}

export const DataSourceManagementView: React.FC<DataSourceManagementViewProps> = ({ onNavigateToView }) => {
  const [sources, setSources] = useState<DataSource[]>(dataSourceEngine.getSources());
  const [alerts, setAlerts] = useState<DataSourceAlert[]>(dataSourceEngine.getAlerts());
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pingResult, setPingResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

  // Form state for new data source
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<DataSourceCategory>('FX_PROVIDER');
  const [newProviderOrg, setNewProviderOrg] = useState('');
  const [newEndpointUrl, setNewEndpointUrl] = useState('');
  const [newFrequency, setNewFrequency] = useState<'REAL_TIME' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY'>('DAILY');
  const [newReliability, setNewReliability] = useState<number>(99.0);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');

  const filteredSources = sources.filter((s) => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.code.toLowerCase().includes(searchTerm.toLowerCase()) || s.providerOrg.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'ALL' || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleTestPing = (id: string) => {
    const res = dataSourceEngine.testPingSource(id);
    setSources([...dataSourceEngine.getSources()]);
    setPingResult({ id, success: res.success, message: res.message });
    setTimeout(() => setPingResult(null), 4000);
  };

  const handleToggleStatus = (id: string, currentStatus: DataSourceStatus) => {
    const nextStatus: DataSourceStatus = currentStatus === 'ACTIVE' ? 'DEACTIVATED' : 'ACTIVE';
    dataSourceEngine.setSourceStatus(id, nextStatus);
    setSources([...dataSourceEngine.getSources()]);
    setAlerts([...dataSourceEngine.getAlerts()]);
  };

  const handleAddSourceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dataSourceEngine.addSource({
      code: newCode.toUpperCase(),
      name: newName,
      category: newCategory,
      providerOrg: newProviderOrg,
      endpointUrl: newEndpointUrl,
      updateFrequency: newFrequency,
      reliabilityScore: newReliability,
      latencyMs: Math.floor(Math.random() * 100 + 50),
      status: 'ACTIVE',
      contactName: newContactName,
      contactEmail: newContactEmail,
      notes: 'Manually provisioned via YamooPay Data Source Management System.',
    });
    setSources([...dataSourceEngine.getSources()]);
    setIsAddModalOpen(false);
    // Reset form
    setNewCode('');
    setNewName('');
    setNewProviderOrg('');
    setNewEndpointUrl('');
    setNewContactName('');
    setNewContactEmail('');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-2xs flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#0E7044] flex items-center justify-center border border-emerald-200">
              <Database className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-black text-[#1A1A1A]">Gestion des Sources de Données YamooPay</h1>
                <span className="px-3 py-1 bg-emerald-50 text-[#0E7044] text-xs font-bold rounded-full border border-emerald-200">
                  {sources.filter((s) => s.status === 'ACTIVE').length} Actives / {sources.length} Total
                </span>
              </div>
              <p className="text-sm text-[#4B5563] mt-1">
                Supervision en temps réel, métadonnées de fiabilité, fréquences de synchronisation et alertes de fraîcheur pour les flux FX, fiscaux et bancaires.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-5 py-3 bg-[#0E7044] hover:bg-[#0B5C37] text-white font-bold text-sm rounded-xl transition-all shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter une source</span>
            </button>
          </div>
        </div>

        {/* Alerts Banner */}
        {alerts.filter((a) => a.status === 'OPEN' || a.status === 'INVESTIGATING').length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 shadow-2xs">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <h3 className="font-bold text-base text-amber-900">Alertes de Fraîcheur & Anomalies de Sources</h3>
            </div>
            <div className="space-y-2">
              {alerts
                .filter((a) => a.status === 'OPEN' || a.status === 'INVESTIGATING')
                .map((alert) => (
                  <div key={alert.id} className="bg-white rounded-xl p-4 border border-amber-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded">
                          {alert.severity}
                        </span>
                        <span className="font-bold text-sm text-[#1A1A1A]">{alert.sourceName}</span>
                        <span className="text-xs text-[#9CA3AF]">• {alert.detectedAt}</span>
                      </div>
                      <p className="text-xs text-[#4B5563] mt-1">{alert.message}</p>
                    </div>
                    <button
                      onClick={() => {
                        dataSourceEngine.resolveAlert(alert.id);
                        setAlerts([...dataSourceEngine.getAlerts()]);
                      }}
                      className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-[#0E7044] text-xs font-bold rounded-lg transition-colors cursor-pointer border border-emerald-200 shrink-0"
                    >
                      Résoudre l&apos;alerte
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {pingResult && (
          <div className={`p-4 rounded-xl border text-sm font-medium flex items-center gap-2 ${pingResult.success ? 'bg-emerald-50 border-emerald-200 text-[#0E7044]' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {pingResult.success ? <CheckCircle className="w-5 h-5 shrink-0" /> : <AlertTriangle className="w-5 h-5 shrink-0" />}
            <span>{pingResult.message}</span>
          </div>
        )}

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-2xs flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="Rechercher par nom, code ou fournisseur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A]"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
            {['ALL', 'FX_PROVIDER', 'TAX_AUTHORITY', 'FINANCIAL_INSTITUTION', 'CENTRAL_BANK', 'COMPLIANCE_FEED'].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3.5 py-2 rounded-xl font-bold text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  categoryFilter === cat
                    ? 'bg-[#0E7044] text-white shadow-xs'
                    : 'bg-[#F8FAFC] text-[#4B5563] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                }`}
              >
                {cat === 'ALL' ? 'Toutes catégories' : cat.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Sources Table / Cards */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-xs font-bold text-[#4B5563] uppercase tracking-wider">
                  <th className="py-4 px-6">Source & Code</th>
                  <th className="py-4 px-6">Catégorie / Fournisseur</th>
                  <th className="py-4 px-6">Fréquence & Sync</th>
                  <th className="py-4 px-6">Fiabilité & Latence</th>
                  <th className="py-4 px-6">Statut</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0] text-sm">
                {filteredSources.map((source) => (
                  <tr key={source.id} className="hover:bg-[#F8FAFC]/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-bold text-[#1A1A1A]">{source.name}</div>
                      <div className="text-xs text-[#9CA3AF] font-mono mt-0.5">{source.code} • {source.endpointUrl}</div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2.5 py-1 bg-[#F1F5F9] text-[#334155] font-bold text-xs rounded-lg">
                        {source.category.replace('_', ' ')}
                      </span>
                      <div className="text-xs text-[#4B5563] mt-1">{source.providerOrg}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-[#1A1A1A]">{source.updateFrequency}</div>
                      <div className="text-xs text-[#9CA3AF]">Synchro: {source.lastSyncedAt}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-xs ${source.reliabilityScore >= 99 ? 'text-[#0E7044]' : source.reliabilityScore >= 97 ? 'text-amber-600' : 'text-red-600'}`}>
                          {source.reliabilityScore}%
                        </span>
                        <span className="text-xs text-[#9CA3AF]">({source.latencyMs}ms)</span>
                      </div>
                      {source.errorCount > 0 && <div className="text-xs text-red-600 mt-0.5">{source.errorCount} erreurs</div>}
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                          source.status === 'ACTIVE'
                            ? 'bg-emerald-50 text-[#0E7044] border border-emerald-200'
                            : source.status === 'DEGRADED'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {source.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right space-x-2">
                      <button
                        onClick={() => handleTestPing(source.id)}
                        className="px-3 py-1.5 bg-[#F8FAFC] hover:bg-[#F1F5F9] text-[#0E7044] font-bold text-xs rounded-xl border border-[#E2E8F0] transition-colors cursor-pointer inline-flex items-center gap-1"
                        title="Tester le flux"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Tester</span>
                      </button>
                      <button
                        onClick={() => handleToggleStatus(source.id, source.status)}
                        className={`px-3 py-1.5 font-bold text-xs rounded-xl border transition-colors cursor-pointer inline-flex items-center gap-1 ${
                          source.status === 'ACTIVE'
                            ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                            : 'bg-emerald-50 hover:bg-emerald-100 text-[#0E7044] border-emerald-200'
                        }`}
                      >
                        <Power className="w-3.5 h-3.5" />
                        <span>{source.status === 'ACTIVE' ? 'Désactiver' : 'Activer'}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add DataSource Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] w-full max-w-lg overflow-hidden relative">
              <div className="bg-[#0E7044] px-6 py-5 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Database className="w-6 h-6 text-white" />
                  <h3 className="font-bold text-lg">Ajouter une Nouvelle Source de Données</h3>
                </div>
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <form onSubmit={handleAddSourceSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Code Unique</label>
                    <input
                      type="text"
                      required
                      placeholder="ex. FX-BCEAO-LIVE"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Catégorie</label>
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value as DataSourceCategory)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm bg-white focus:ring-2 focus:ring-[#0E7044]"
                    >
                      <option value="FX_PROVIDER">FX Provider</option>
                      <option value="TAX_AUTHORITY">Tax Authority</option>
                      <option value="FINANCIAL_INSTITUTION">Financial Institution</option>
                      <option value="CENTRAL_BANK">Central Bank</option>
                      <option value="MARKET_DATA_API">Market Data API</option>
                      <option value="COMPLIANCE_FEED">Compliance Feed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Nom de la Source</label>
                  <input
                    type="text"
                    required
                    placeholder="ex. Banque Centrale des États de l'Afrique de l'Ouest"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Organisation Fournisseur</label>
                    <input
                      type="text"
                      required
                      placeholder="ex. BCEAO"
                      value={newProviderOrg}
                      onChange={(e) => setNewProviderOrg(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Fréquence de Synchro</label>
                    <select
                      value={newFrequency}
                      onChange={(e) => setNewFrequency(e.target.value as any)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm bg-white focus:ring-2 focus:ring-[#0E7044]"
                    >
                      <option value="REAL_TIME">Real Time</option>
                      <option value="HOURLY">Hourly</option>
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">URL de l&apos;Endpoint API</label>
                  <input
                    type="url"
                    required
                    placeholder="https://api.bceao.int/v1/rates"
                    value={newEndpointUrl}
                    onChange={(e) => setNewEndpointUrl(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Contact (Nom)</label>
                    <input
                      type="text"
                      required
                      placeholder="ex. Support Technique"
                      value={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Contact (E-mail)</label>
                    <input
                      type="email"
                      required
                      placeholder="tech@bceao.int"
                      value={newContactEmail}
                      onChange={(e) => setNewContactEmail(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[#D1D5DB] text-sm focus:ring-2 focus:ring-[#0E7044]"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-[#E2E8F0] flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="px-4 py-2 bg-[#F1F5F9] text-[#334155] font-bold text-sm rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white font-bold text-sm rounded-xl transition-colors shadow-xs cursor-pointer"
                  >
                    Enregistrer la Source
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
